/**
 * TenantProvider — Frontend tenancy boot layer.
 *
 * On app startup:
 * 1. Calls GET /api/tenant/resolve to determine mode (platform vs tenant)
 * 2. Sets mode + tenant context for the entire app
 * 3. Injects X-Tenant-Slug / X-Tenant-Mode headers on all API calls
 *
 * In production:
 *   app.rapidxtech.com.au     → platform mode
 *   bsms.rapidxtech.com.au    → tenant mode (slug=bsms)
 *
 * In preview/dev:
 *   ?tenant=bsms               → tenant mode (forces slug)
 *   ?mode=platform              → platform mode
 *   No params                   → server decides (default bsms)
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TenantContext = createContext(null);

export const useTenant = () => {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used within TenantProvider');
  return ctx;
};

/**
 * Detect tenant slug from browser hostname for production domains.
 * Returns { mode, slug } or null if not a production domain.
 */
function detectFromHostname() {
  const hostname = window.location.hostname.toLowerCase();
  const PRODUCTION_BASES = ['rapidx.com', 'rapidxtech.com.au'];
  const RESERVED = new Set(['app', 'platform', 'admin', 'www', 'api']);

  for (const base of PRODUCTION_BASES) {
    if (hostname.endsWith(`.${base}`)) {
      const sub = hostname.slice(0, -(base.length + 1));
      const candidate = sub.split('.')[0];
      if (RESERVED.has(candidate)) return { mode: 'platform', slug: null };
      if (candidate) return { mode: 'tenant', slug: candidate };
    }
    if (hostname === base) return { mode: 'platform', slug: null };
  }
  return null; // Not a production domain — use API resolution
}

/**
 * In preview/dev, check URL params for tenant overrides.
 */
function detectFromUrlParams() {
  const params = new URLSearchParams(window.location.search);
  if (params.get('mode') === 'platform') return { mode: 'platform', slug: null };
  const slugParam = params.get('tenant');
  if (slugParam) return { mode: 'tenant', slug: slugParam };
  return null;
}

export function TenantProvider({ children }) {
  const [tenantState, setTenantState] = useState({
    mode: null,       // "platform" | "tenant"
    slug: null,       // e.g. "bsms"
    tenant: null,     // full tenant object { id, name, slug, branding, features }
    error: null,      // error message if tenant not found
    loading: true,
  });

  useEffect(() => {
    let cancelled = false;

    async function boot() {
      // Step 1: Try client-side detection (production domains)
      let detected = detectFromHostname() || detectFromUrlParams();

      // Step 2: Build headers for the resolve call
      const headers = {};
      if (detected) {
        if (detected.mode === 'platform') {
          headers['X-Tenant-Mode'] = 'platform';
        } else if (detected.slug) {
          headers['X-Tenant-Slug'] = detected.slug;
        }
      }

      // Step 3: Call server to resolve and get tenant config
      try {
        const { data } = await axios.get('/api/tenant/resolve', { headers });

        if (cancelled) return;

        setTenantState({
          mode: data.mode,
          slug: data.tenant?.slug || detected?.slug || null,
          tenant: data.tenant,
          error: data.error || null,
          loading: false,
        });
      } catch (err) {
        if (cancelled) return;
        // If server is unreachable, use client-side detection as fallback
        setTenantState({
          mode: detected?.mode || 'tenant',
          slug: detected?.slug || null,
          tenant: null,
          error: 'Unable to connect to server',
          loading: false,
        });
      }
    }

    boot();
    return () => { cancelled = true; };
  }, []);

  // Provide helper for API calls to include tenant headers
  const getTenantHeaders = () => {
    const headers = {};
    if (tenantState.mode === 'platform') {
      headers['X-Tenant-Mode'] = 'platform';
    } else if (tenantState.slug) {
      headers['X-Tenant-Slug'] = tenantState.slug;
    }
    return headers;
  };

  return (
    <TenantContext.Provider value={{
      ...tenantState,
      isPlatform: tenantState.mode === 'platform',
      isTenant: tenantState.mode === 'tenant' && !!tenantState.tenant,
      getTenantHeaders,
    }}>
      {children}
    </TenantContext.Provider>
  );
}
